import React, { useState, useEffect, useRef } from 'react';
import { FiAlertCircle, FiSearch, FiRefreshCw } from 'react-icons/fi';
import { RiFlaskLine } from 'react-icons/ri';
import ProductResultCard from './ProductResultCard';
import Pagination from '../Pagination';

/**
 * Skeleton loading placeholder for result cards.
 * Displays animated pulse blocks mimicking the ProductResultCard layout.
 */
function ResultSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="p-4 border rounded-xl bg-white/40 border-white/60 animate-pulse"
        >
          <div className="flex">
            <div className="flex-shrink-0 mr-3">
              <div className="w-10 h-10 rounded-full bg-secondary/10" />
            </div>
            <div className="flex-1 space-y-3">
              <div className="w-3/4 h-4 rounded bg-secondary/10" />
              <div className="w-1/2 h-3 rounded bg-secondary/8" />
              <div className="flex gap-2 mt-2">
                <div className="w-16 h-6 rounded-full bg-secondary/8" />
                <div className="w-20 h-6 rounded-full bg-secondary/8" />
                <div className="w-14 h-6 rounded-full bg-secondary/8" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Search results panel that displays product results, loading states,
 * error states, and empty/prompt states for the ingredient search feature.
 *
 * @param {Object} props
 * @param {Array} props.results - Array of product objects to display
 * @param {Object} props.pagination - Pagination info {page, totalItems, totalPages}
 * @param {boolean} props.isLoading - Whether a search request is in progress
 * @param {string|null} props.error - Error message if search failed
 * @param {'and'|'or'} props.mode - Current search mode
 * @param {string[]} props.selectedIngredientUris - Selected ingredient URIs for highlighting
 * @param {Function} props.onPageChange - Callback when page changes (receives 0-based page index)
 * @param {Function} props.onRetry - Callback to retry failed search
 * @param {boolean} props.hasIngredients - Whether any ingredients are selected
 */
export default function SearchResultsPanel({
  results = [],
  pagination = { page: 1, totalItems: 0, totalPages: 0 },
  isLoading = false,
  error = null,
  mode = 'and',
  selectedIngredientUris = [],
  onPageChange,
  onRetry,
  hasIngredients = false,
}) {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const skeletonTimerRef = useRef(null);

  // Show skeleton loading indicator within 200ms of request start
  useEffect(() => {
    if (isLoading) {
      skeletonTimerRef.current = setTimeout(() => {
        setShowSkeleton(true);
      }, 200);
    } else {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
        skeletonTimerRef.current = null;
      }
      setShowSkeleton(false);
    }

    return () => {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
      }
    };
  }, [isLoading]);

  // Sort results alphabetically by product name (case-insensitive)
  const sortedResults = [...results].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );

  const modeLabel =
    mode === 'and'
      ? 'Showing products with ALL ingredients'
      : 'Showing products with ANY ingredient';

  // State: No ingredients selected - show prompt
  if (!hasIngredients) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100/50">
          <RiFlaskLine className="size-7 text-emerald-600/60" />
        </div>
        <h3 className="mb-2 text-18 font-medium text-secondary/80">
          Search by Ingredients
        </h3>
        <p className="max-w-sm text-14 text-secondary/60 leading-relaxed">
          Start by selecting one or more ingredients above to find products that contain them.
        </p>
      </div>
    );
  }

  // State: Loading with skeleton
  if (isLoading && showSkeleton) {
    return (
      <div>
        <div className="mb-4">
          <p className="text-14 text-secondary/60">{modeLabel}</p>
        </div>
        <ResultSkeleton />
      </div>
    );
  }

  // State: Loading but skeleton not yet shown (within 200ms window)
  if (isLoading) {
    return (
      <div>
        <div className="mb-4">
          <p className="text-14 text-secondary/60">{modeLabel}</p>
        </div>
      </div>
    );
  }

  // State: Error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-red-50">
          <FiAlertCircle className="size-6 text-red-500" />
        </div>
        <h3 className="mb-2 text-16 font-medium text-secondary/80">
          Search Failed
        </h3>
        <p className="max-w-sm mb-4 text-14 text-secondary/60 leading-relaxed">
          {error}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 text-14 font-medium text-white transition-colors rounded-full bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-600/30"
        >
          <FiRefreshCw className="size-4" />
          Retry
        </button>
      </div>
    );
  }

  // State: No results
  if (sortedResults.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-secondary/5">
          <FiSearch className="size-6 text-secondary/40" />
        </div>
        <h3 className="mb-2 text-16 font-medium text-secondary/80">
          No Products Found
        </h3>
        <p className="max-w-sm text-14 text-secondary/60 leading-relaxed">
          {mode === 'and'
            ? 'No products contain all of the selected ingredients. Try switching to "ANY" mode to find products with at least one of your selected ingredients.'
            : 'No products match any of the selected ingredients. Try adjusting your selection.'}
        </p>
      </div>
    );
  }

  // State: Results
  return (
    <div>
      {/* Header: mode label and result count */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="text-14 text-secondary/60">{modeLabel}</p>
        <p className="text-14 font-medium text-secondary/70">
          Found {pagination.totalItems} product{pagination.totalItems !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Results list */}
      <div className="space-y-3">
        {sortedResults.map((product) => (
          <ProductResultCard
            key={product.uri}
            product={product}
            selectedIngredientUris={selectedIngredientUris}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            pageCount={pagination.totalPages}
            currentPage={pagination.page - 1}
            onPageChange={(zeroBasedPage) => onPageChange?.(zeroBasedPage + 1)}
          />
        </div>
      )}
    </div>
  );
}
